import { useOrganizationList } from '@clerk/nextjs';
import { Form, Label, V2 } from '@firmable/common';
import { ChevronLeft, Logo } from '@firmable/icons';
import { Routes } from '@web/constants/routes';
import { useRouter } from 'next/router';
import React from 'react';
import { useForm } from 'react-hook-form';

interface FormValues extends Record<string, unknown> {
  companyName: string;
}

export interface CreateCompanyFormProps {
  onBack?: () => void;
}

export const CreateCompanyForm = ({ onBack }: CreateCompanyFormProps) => {
  const router = useRouter();
  const { createOrganization, isLoaded, setActive } = useOrganizationList();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      companyName: '',
    },
  });

  const onSubmit = async (data: FormValues) => {
    const trimmedName = data.companyName.trim();

    if (!createOrganization || !setActive) {
      setError('companyName', { message: 'Unable to create company. Please try again.' });
      return;
    }

    try {
      const org = await createOrganization({ name: trimmedName });
      // Set the new org as active so the session token includes the orgId
      await setActive({ organization: org.id });
      router.push(Routes.planSelectionPath);
    } catch {
      setError('companyName', { message: 'Failed to create company. Please try again.' });
    }
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <div className="bg-neutral-50 flex flex-col gap-6 items-center w-[400px] sm:w-[440px] px-6 sm:px-10 py-8 rounded-2xl">
      {/* Logo */}
      <Logo.Light.FirmableFull height={30} width={142} />

      {/* Header */}
      <div className="flex flex-col gap-2 relative items-center text-center w-full">
        {onBack ? (
          <V2.ButtonIcon
            variant="tertiary"
            size="sm"
            onClick={onBack}
            className="absolute top-[-50px] left-0">
            <ChevronLeft size={16} />
          </V2.ButtonIcon>
        ) : null}
        <h1 className="font-semibold text-[30px] leading-[40px] text-neutral-800">Almost there</h1>
        <p className="text-sm text-neutral-500">Let us know just a little more about you.</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6 w-full">
        <div className="pb-[6px]">
          <Label.Info>Company name</Label.Info>
          <Form.Input
            name="companyName"
            register={register}
            options={{ required: 'Company name is required' }}
            error={errors.companyName}
            placeholder="Enter your company name"
            className="w-full"
            disabled={isSubmitting}
          />
        </div>

        <V2.Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={isSubmitting}
          className="!w-full !h-11 !bg-brand-1-600 hover:!bg-brand-1-700 !text-white !font-semibold !text-base !rounded-lg shadow-btn-bevel">
          {isSubmitting ? 'Creating...' : 'Continue'}
        </V2.Button>
      </form>
    </div>
  );
};
